import React from 'react';
import {
  Button, TextInput, Textarea, ErrorState, Badge, Alert, FilterChip,
} from '@parsina/framework';
import { AlertTriangle, History, Check } from 'lucide-react';
import { C, CT } from '../../../constants/theme';
import { LTR_TEXT } from '../../../i18n/I18nProvider.jsx';
import Panel from '../../../components/common/Panel.jsx';
import { SubjectivePanelSkeleton, PriorVisitsSkeleton } from '../Visit.skeleton.jsx';
import VoiceDictateButton from '../../../components/common/VoiceDictateButton.jsx';
import { listSeparator } from '../../../i18n/format';

// §3.1.2 — Subjective (SOAP note), the MIDDLE of the persistent column.
//
// Three requirements, three regions:
//
//   "Structured input fields for current complaints"   → the form
//   "Integrated display of «شرح حال بیمار»"             → the history strip
//   "Contextual linking to previous visits"            → the prior-visit list
//
// ── Why the fields are structured AND composed into prose ──────────────────
// What is STORED is a SOAP note with the structured dental fields inside its
// content object (the service allows extra keys), and the readable `subjective`
// string is derived from them server-side on every save. So the dentist fills
// in fields, and a reader that knows nothing about dentistry — the doctor app,
// a FHIR export — still finds a real note rather than an empty S.
//
// ── Saving ─────────────────────────────────────────────────────────────────
// Explicit, not on blur and not on a timer. A note is a clinical record; a
// dentist mid-sentence must not be committing drafts, and an autosave racing a
// half-typed complaint is how «درد» ends up in the record as «د».

const PAIN_CHARACTERS = ['sharp', 'dull', 'throbbing', 'sensitivity', 'none'];

/** The empty form. Every field a string so the inputs are always controlled. */
const EMPTY = {
  chiefComplaint: '', painScore: '', painCharacter: '',
  onset: '', duration: '', aggravatingFactors: '', relievingFactors: '',
  dentalHistory: '', notes: '',
};

/** A saved note → the form's shape. */
function toForm(note) {
  const d = note?.dental || {};
  return {
    chiefComplaint: d.chiefComplaint || '',
    // 0 is a real score and must survive the || fallback that would blank it.
    painScore: d.painScore == null ? '' : String(d.painScore),
    painCharacter: d.painCharacter || '',
    onset: d.onset || '',
    duration: d.duration || '',
    aggravatingFactors: d.aggravatingFactors || '',
    relievingFactors: d.relievingFactors || '',
    dentalHistory: d.dentalHistory || '',
    notes: d.notes || '',
  };
}

const Field = ({ label, children }) => (
  <label style={{ display: 'block' }}>
    <span style={{
      display: 'block', fontSize: 11.5, fontWeight: 700,
      color: C.textSecondary, marginBottom: 4,
    }}>
      {label}
    </span>
    {children}
  </label>
);

// The kit's FilterChip, not a hand-rolled pill: it is already `aria-pressed`
// and already `min-h-11`, which is the touch floor this section renders at on a
// phone (this file is shared by both surfaces).
const Chip = ({ active, onClick, disabled, children }) => (
  <FilterChip active={active} disabled={disabled} onClick={onClick} label={children} />
);

export default function VisitSubjective({
  t, formatNumber, formatDate, note, editable, selectedTooth,
  /**
   * The last tooth CLICKED on the arch, as `{ fdi, seq }`.
   *
   * §3.1.3's link runs both ways, and the way a dentist names the teeth a
   * complaint is about is by pointing at them — several of them. `seq` is what
   * makes that possible: the chart holds ONE `selectedTooth`, so clicking a
   * tooth that is already selected changes nothing observable, and a toggle
   * driven off the value alone could never take a tooth back OUT of the list.
   * The counter turns each click into a distinct event.
   */
  toothPick,
  priorVisits, onOpenPriorVisit, patient,
  /**
   * A ref the visit screen owns, kept pointing at { dirty, submit }.
   *
   * «پایان جلسه» closes the visit for good, and this form is the ONLY thing on
   * the screen holding text that is not already persisted — every other section
   * writes on its own action. So the screen has to be able to ask this one
   * whether it has anything pending and to flush it, without lifting the whole
   * form's state out of here.
   */
  pendingRef,
  // The phone stacks all three persistent sections in one scroll, so the two
  // text-heavy ones fold. Forwarded to Panel rather than wrapped in a
  // CollapsibleSection, which would render the title twice.
  collapsible = false, defaultOpen = true,
}) {
  const {
    note: saved, revision, loading, error, saving, save, reload,
  } = note;

  const [form, setForm] = React.useState(EMPTY);
  const [dirty, setDirty] = React.useState(false);
  const [teeth, setTeeth] = React.useState([]);
  const [failure, setFailure] = React.useState(null);
  const [justSaved, setJustSaved] = React.useState(false);

  // ── Words a dictation session has produced but not finished ──────────────
  // Kept OUT of `form` and shown only by concatenation: the server engine's
  // final message carries the whole session rather than its tail, so anything
  // folded into the form here would be written a second time when it lands —
  // and, worse, would be `dirty` and saveable while still provisional.
  // `VoiceDictateButton` clears it back to '' in the same render as the final
  // text reaches `onText`.
  //
  // یادداشت ONLY, and not شکایت اصلی, which is a single-line input: a growing
  // preview scrolls out of the left of one of those as it arrives, so the
  // dentist would watch the FIRST words of a sentence whose newest words are
  // the ones they want to check. A textarea grows downward and can be pinned to
  // its bottom (below), so there the preview is readable. The complaint still
  // gets the whole transcript the moment the session ends, as it always did.
  const [liveNotes, setLiveNotes] = React.useState('');

  // Keeps the newest streamed words in view in a box that is two rows tall.
  // React 19 passes `ref` as an ordinary prop, and the kit's Textarea spreads
  // its rest props onto the real control, so this reaches the <textarea>.
  const notesRef = React.useRef(null);
  React.useLayoutEffect(() => {
    if (liveNotes && notesRef.current) {
      notesRef.current.scrollTop = notesRef.current.scrollHeight;
    }
  }, [liveNotes]);

  // Load the saved note into the form once per REVISION, and never over unsaved
  // edits: a background reload that overwrote what the dentist is typing would
  // lose the complaint mid-sentence.
  //
  // Keyed on `revision` and not on `saved.id`, because the id does NOT change
  // when the note is revised in place — and this note is written from two
  // places. Answering a question in پرسش‌ها appends a finding to it; on the id
  // alone this form never re-synced, the dentist never saw the finding, and
  // their next «ذخیره» wrote the stale text back over it.
  const loadedRef = React.useRef(null);
  React.useEffect(() => {
    const key = `${saved?.id || 'none'}:${revision ?? 0}`;
    if (loadedRef.current === key) return;
    // Unsaved edits still win: the dentist's half-typed complaint is not
    // discarded by somebody else's write. `justSaved` keeps the confirmation
    // visible for the save that produced this revision.
    if (dirty) return;
    loadedRef.current = key;
    setForm(toForm(saved));
    setTeeth(saved?.dental?.teethFdi || []);
  }, [saved, revision, dirty]);

  const set = (key) => (value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setDirty(true);
    setJustSaved(false);
  };

  // §3.1.3's link runs both ways: a tooth selected on the odontogram can be
  // attached to the complaint, so «کدام دندان» is answered by pointing rather
  // than by typing a number. Clicking the arch now does this on its own (see
  // the pick effect below); the button remains for the tooth that is selected
  // but NOT listed — the one whose chip was just removed by ×.
  const attachTooth = () => {
    if (!selectedTooth || teeth.includes(selectedTooth)) return;
    setTeeth((prev) => [...prev, selectedTooth]);
    setDirty(true);
    setJustSaved(false);
  };
  const detachTooth = (fdi) => {
    setTeeth((prev) => prev.filter((x) => x !== fdi));
    setDirty(true);
    setJustSaved(false);
  };

  // ── Clicking teeth on the arch fills this list ────────────────────────────
  // The chart is single-select — it has to be, because the drawer, the tooth
  // history and «افزودن به طرح درمان» all speak about ONE tooth — so before
  // this a dentist who picked three teeth on the arch could only ever get the
  // last of them into «دندان‌های درگیر», and only by pressing a button two
  // sections further down. Now each click toggles its tooth here, and the
  // chip's × still removes one.
  //
  // Keyed on `seq`, not on the tooth: a second click on the SAME tooth must be
  // heard, and re-running on a re-render must not silently re-add a tooth the
  // dentist just removed.
  const lastPickRef = React.useRef(0);
  React.useEffect(() => {
    const seq = toothPick?.seq || 0;
    const fdi = toothPick?.fdi;
    if (!seq || !fdi || seq === lastPickRef.current) return;
    // Not before the saved note has landed: marking the form dirty first would
    // block the load above FOREVER (`if (dirty) return`) and the next «ذخیره»
    // would overwrite a real subjective with this blank one. The pick is HELD
    // rather than dropped — `loading` is a dependency, so it lands the moment
    // the note does, and a dentist who tapped a tooth while the panel was still
    // spinning does not have to notice that it did nothing.
    if (loading || loadedRef.current === null) return;
    // A closed visit is read-only; the arch is still browsable there.
    if (!editable) return;
    lastPickRef.current = seq;
    setTeeth((prev) => (prev.includes(fdi) ? prev.filter((x) => x !== fdi) : [...prev, fdi]));
    setDirty(true);
    setJustSaved(false);
  }, [toothPick, editable, loading]);

  const submit = async () => {
    setFailure(null);
    const score = form.painScore === '' ? null : Number(form.painScore);
    const err = await save({
      subjective: {
        chiefComplaint: form.chiefComplaint || null,
        painScore: Number.isFinite(score) ? score : null,
        painCharacter: form.painCharacter || null,
        teethFdi: teeth,
        onset: form.onset || null,
        duration: form.duration || null,
        aggravatingFactors: form.aggravatingFactors || null,
        relievingFactors: form.relievingFactors || null,
        dentalHistory: form.dentalHistory || null,
        notes: form.notes || null,
      },
    });
    if (err) {
      setFailure(err);
      return err;
    }
    setDirty(false);
    setJustSaved(true);
    // Returns the error, or null — «پایان جلسه» flushes through this and must
    // NOT close the visit when the flush failed. Closing is one-way, so a note
    // that did not land before it would be lost with no way back in.
    return null;
  };

  // No dependency list: `submit` closes over the current form and teeth, so a
  // stale entry here would flush yesterday's text on «پایان جلسه».
  React.useEffect(() => {
    if (!pendingRef) return undefined;
    pendingRef.current = { dirty, submit };
    return () => { pendingRef.current = null; };
  });

  // This panel alone, in its own shape — the history strip, the complaint
  // field, the tooth chips, the score fields — inside the real Panel
  // (DESIGN.md §"Loading & skeleton", region load).
  if (loading) {
    return (
      <SubjectivePanelSkeleton
        t={t}
        label={t('common.loading')}
        collapsible={collapsible}
        defaultOpen={defaultOpen}
      />
    );
  }

  if (error) {
    return (
      <Panel title={t('visit.subjective.title')}>
        <div role="alert">
          <ErrorState
            title={t('visit.subjective.error.title')}
            description={t('visit.subjective.error.body')}
            onRetry={reload}
            retryLabel={t('common.actions.retry')}
          />
        </div>
      </Panel>
    );
  }

  const allergies = patient?.allergies || [];
  const conditions = patient?.conditions || [];
  const medications = patient?.medications || [];

  return (
    <Panel
      title={t('visit.subjective.title')}
      collapsible={collapsible}
      defaultOpen={defaultOpen}
      action={
        editable ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {justSaved && !dirty && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                fontSize: 11.5, color: CT.goodInk,
              }}>
                <Check size={13} aria-hidden />
                {t('visit.subjective.saved')}
              </span>
            )}
            <Button className="min-h-11" onClick={submit} disabled={saving || !dirty}>
              {saving ? t('common.actions.saving') : t('common.actions.save')}
            </Button>
          </div>
        ) : (
          <Badge variant="outline">{t('visit.readOnly.badge')}</Badge>
        )
      }
    >
      <div data-testid="visit-subjective" data-state={saved ? 'content' : 'empty'}>
        {failure && (
          <Alert variant="error" style={{ marginBottom: 12 }}>
            {t('visit.subjective.saveFailed')}
          </Alert>
        )}

        {/* «شرح حال بیمار» — the integrated history display §3.1.2 asks for.
            Allergies lead and are the only part allowed to be red: it is the
            one fact here that changes what may be prescribed two tabs away. */}
        <div style={{
          background: CT.surface2, borderRadius: 10, padding: '10px 12px', marginBottom: 14,
        }}>
          <div style={{
            fontSize: 11.5, fontWeight: 800, color: C.textSecondary, marginBottom: 6,
          }}>
            {t('visit.subjective.history')}
          </div>
          {allergies.length > 0 ? (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6,
              color: CT.dangerInk, fontSize: 12.5, fontWeight: 700,
            }}>
              <AlertTriangle size={14} aria-hidden />
              <span>{allergies.map((a) => a.allergen).filter(Boolean).join(' · ')}</span>
            </div>
          ) : (
            <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 6 }}>
              {t('visit.subjective.noAllergies')}
            </div>
          )}
          <div style={{ fontSize: 12, color: C.textSecondary, lineHeight: 1.9 }}>
            <div>
              <strong>{t('visit.subjective.conditions')}{t('common.punct.colon')} </strong>
              {conditions.length
                ? conditions.map((c) => c.display).filter(Boolean).join(listSeparator())
                : t('common.labels.none')}
            </div>
            <div>
              <strong>{t('visit.subjective.medications')}{t('common.punct.colon')} </strong>
              {medications.length
                ? medications.map((m) => m.display).filter(Boolean).join(listSeparator())
                : t('common.labels.none')}
            </div>
          </div>
        </div>

        {/* The structured complaint. */}
        <div style={{ display: 'grid', gap: 10 }}>
          {/* The one field a dentist fills while still TALKING to the patient,
              before any instrument is in the mouth — so it is the one that
              carries dictation. The mic hides itself where the browser has no
              recogniser; see VoiceDictateButton. */}
          <Field label={t('visit.subjective.chiefComplaint')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <TextInput
                  dir="auto"
                  value={form.chiefComplaint}
                  disabled={!editable}
                  aria-label={t('visit.subjective.chiefComplaint')}
                  onChange={(e) => set('chiefComplaint')(e.target.value)}
                />
              </div>
              <VoiceDictateButton
                disabled={!editable}
                label={t('visit.subjective.dictateComplaint')}
                onText={(text) => set('chiefComplaint')(
                  // Append: dictation adds to what is already there rather
                  // than discarding a complaint the dentist half-typed.
                  form.chiefComplaint ? `${form.chiefComplaint} ${text}` : text,
                )}
              />
            </div>
          </Field>

          {/* The teeth the complaint is about, filled by POINTING at the arch
              above rather than by typing an FDI number. */}
          <Field label={t('visit.subjective.teeth')}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
              {teeth.length === 0 && (
                <span style={{ fontSize: 12, color: C.textMuted }}>
                  {t('visit.subjective.noTeeth')}
                </span>
              )}
              {teeth.map((fdi) => (
                <Chip key={fdi} active disabled={!editable} onClick={() => detachTooth(fdi)}>
                  <span {...LTR_TEXT}>{formatNumber(fdi)}</span>
                  {editable && <span style={{ marginInlineStart: 5 }}>×</span>}
                </Chip>
              ))}
              {editable && (
                <Button
                  variant="subtle"
                  className="min-h-11"
                  onClick={attachTooth}
                  disabled={!selectedTooth || teeth.includes(selectedTooth)}
                >
                  {selectedTooth
                    ? t('visit.subjective.addTooth', { fdi: formatNumber(selectedTooth) })
                    : t('visit.subjective.selectToothFirst')}
                </Button>
              )}
            </div>
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
            <Field label={t('visit.subjective.painScore')}>
              <TextInput
                type="number"
                min={0}
                max={10}
                dir="auto"
                value={form.painScore}
                disabled={!editable}
                aria-label={t('visit.subjective.painScore')}
                onChange={(e) => set('painScore')(e.target.value)}
              />
            </Field>
            {/* A real time control, not free text: «زمان شروع» is a clock time
                and the native picker keeps the stored value a machine-readable
                HH:MM instead of ten spellings of «نه شب». The kit TextInput
                forwards `type` to the input (§17.2), same as painScore's
                number. LTR: a time is digits regardless of UI language. */}
            <Field label={t('visit.subjective.onset')}>
              <TextInput type="time" dir="ltr" value={form.onset} disabled={!editable}
                         aria-label={t('visit.subjective.onset')}
                         onChange={(e) => set('onset')(e.target.value)} />
            </Field>
            <Field label={t('visit.subjective.duration')}>
              <TextInput dir="auto" value={form.duration} disabled={!editable}
                         aria-label={t('visit.subjective.duration')}
                         onChange={(e) => set('duration')(e.target.value)} />
            </Field>
          </div>

          <Field label={t('visit.subjective.painCharacter')}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {PAIN_CHARACTERS.map((p) => (
                <Chip
                  key={p}
                  active={form.painCharacter === p}
                  disabled={!editable}
                  onClick={() => set('painCharacter')(form.painCharacter === p ? '' : p)}
                >
                  {t(`visit.subjective.pain.${p}`)}
                </Chip>
              ))}
            </div>
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
            <Field label={t('visit.subjective.aggravating')}>
              <TextInput dir="auto" value={form.aggravatingFactors} disabled={!editable}
                         aria-label={t('visit.subjective.aggravating')}
                         onChange={(e) => set('aggravatingFactors')(e.target.value)} />
            </Field>
            <Field label={t('visit.subjective.relieving')}>
              <TextInput dir="auto" value={form.relievingFactors} disabled={!editable}
                         aria-label={t('visit.subjective.relieving')}
                         onChange={(e) => set('relievingFactors')(e.target.value)} />
            </Field>
          </div>

          <Field label={t('visit.subjective.dentalHistory')}>
            <Textarea rows={2} dir="auto" value={form.dentalHistory} disabled={!editable}
                      aria-label={t('visit.subjective.dentalHistory')}
                      onChange={(e) => set('dentalHistory')(e.target.value)} />
          </Field>

          {/* Dictated `continuous`, unlike شکایت اصلی: this one is a paragraph,
              and a recogniser that closes at the first pause would have the
              dentist pressing the mic once per sentence. */}
          <Field label={t('visit.subjective.notes')}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* `readOnly` only once words are actually streaming in — the
                    engine is silent for the first several seconds of a session
                    and there is nothing to protect from an edit until then. */}
                <Textarea ref={notesRef} rows={2} dir="auto"
                          value={liveNotes ? (form.notes ? `${form.notes} ${liveNotes}` : liveNotes) : form.notes}
                          disabled={!editable} readOnly={!!liveNotes}
                          aria-label={t('visit.subjective.notes')}
                          onChange={(e) => set('notes')(e.target.value)} />
              </div>
              <VoiceDictateButton
                continuous
                disabled={!editable}
                label={t('visit.subjective.dictateNotes')}
                onInterim={setLiveNotes}
                onText={(text) => set('notes')(
                  form.notes ? `${form.notes} ${text}` : text,
                )}
              />
            </div>
          </Field>
        </div>

        {/* «مراجعات قبلی» — §3.1.2's contextual linking. Each row OPENS that
            visit's workspace, which is what makes it a link rather than a
            summary: the question a dentist asks of a previous visit is
            "what did we do", and only the visit itself answers it. */}
        <div style={{ marginTop: 16, borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8,
            fontSize: 11.5, fontWeight: 800, color: C.textSecondary,
          }}>
            <History size={13} aria-hidden />
            {t('visit.subjective.priorVisits')}
          </div>
          {priorVisits.loading ? (
            <PriorVisitsSkeleton label={t('common.loading')} />
          ) : priorVisits.visits.length === 0 ? (
            <span style={{ fontSize: 12, color: C.textMuted }}>
              {t('visit.subjective.noPriorVisits')}
            </span>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {priorVisits.visits.map((v) => (
                // A kit Button rather than a styled <button>: this is a row-
                // sized tap target on a phone, and the kit is what guarantees
                // the 2.75rem floor. `justify-start` because a Button centres
                // by default and this is a line of text, not a label.
                <Button
                  key={v.appointmentId}
                  variant="outline"
                  className="min-h-11 w-full justify-start"
                  onClick={() => onOpenPriorVisit(v.appointmentId)}
                >
                  <span style={{
                    display: 'flex', alignItems: 'center', gap: 8, width: '100%', minWidth: 0,
                  }}>
                    <span style={{ fontSize: 12, color: C.textSecondary, flexShrink: 0 }}>
                      {v.dateTime ? formatDate(v.dateTime, 'numeric') : '—'}
                    </span>
                    <span style={{
                      fontSize: 12.5, color: C.textPrimary, flex: 1, minWidth: 0,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      textAlign: 'start',
                    }}>
                      {v.summary || t('visit.subjective.priorNoSummary')}
                    </span>
                    {v.diagnosisCount > 0 && (
                      <Badge variant="outline">
                        {t('visit.subjective.priorDiagnoses', {
                          count: formatNumber(v.diagnosisCount),
                        })}
                      </Badge>
                    )}
                  </span>
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>
    </Panel>
  );
}
